import { Tour, TourType } from '@/types/tourBooking';
import { Box, Typography } from '@mui/material';
import {
  BookingContainer,
  AdventuresSection,
  ExperiencesSection,
  CategoryHeader,
  TourGrid,
  TourCard,
  TourImage,
  TourContent,
  PriceText,
  BookButton,
  Badge,
  ContentTourText,
  HorizontalScrollGrid,
} from './style';
import { useTranslation } from 'react-i18next';
import { useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { getMinPriceOfTour, getThumbnailImage, getThumbnailImageAlt, queryToDetailTourUrl, regexSlug } from '@/utils/utils';
import { toast } from 'react-toastify';
import { trackTourBookingButtonClick, trackTourBookingStep } from '@/utils/gtag';
import Price from '@/components/Price';

interface Props {
  tours: Tour[];
}

// Example badge logic: you may want to adjust this based on your data
const getBadge = (tour: Tour) => {
  // Example: add badges based on some property or id
  if (tour.id === 'countryside') return 'Giá tốt nhất!';
  if (tour.id === 'countryside-experience') return 'MUST GO';
  return undefined;
};

export default function TourBookingByCustomerHome({ tours }: Props) {

  const { t } = useTranslation('common');
  const { t: tError } = useTranslation('error');
  const router = useRouter();
  const {
    setSelectedTourIdForBooking,
  } = useTourBookingContext();

  // Split tours into two groups
  const experienceTours = tours.filter(tour => tour.tourType === TourType.EXPERIENCE);
  const adventureTours = tours.filter(tour => tour.tourType === TourType.ADVENTURES);
  // Auto-scroll logic for ExperiencesSection
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    let interval: NodeJS.Timeout;
    let cardWidth = 0;
    let scrollTo = 0;
    const getCardWidth = () => {
      const card = container.querySelector('[data-experience-card]');
      if (card) {
        return (card as HTMLElement).offsetWidth + 24; // 24px gap (theme.spacing(3))
      }
      return 340; // fallback
    };
    const scrollNext = () => {
      cardWidth = getCardWidth();
      // If at end, go back to start
      if (container.scrollLeft + container.offsetWidth >= container.scrollWidth - cardWidth) {
        scrollTo = 0;
      } else {
        scrollTo = container.scrollLeft + cardWidth;
      }
      container.scrollTo({ left: scrollTo, behavior: 'smooth' });
    };
    interval = setInterval(scrollNext, 15000);
    return () => clearInterval(interval);
  }, [experienceTours.length]);

  const _onClickBookingTour = (tourType: TourType, tourId: string) => {

    const isSlugValid = regexSlug.test(tourId);

    if (!isSlugValid) {
      toast.error(tError('error.404.slug_error'));
      return;
    }

    setSelectedTourIdForBooking(tourId);

    const currentQuery = router.query;

    if (currentQuery.tab) {
      delete currentQuery.tab;
    }

    router.push({
      pathname: `${tourType}/${tourId}`,
      query: {
        ...currentQuery,
        //tour_id: tourId
        scroll_to_booking: true
      }
    });

    trackTourBookingButtonClick('form_comfirm_book_now', tourId, 'click_form_comfirm_book_now');
    trackTourBookingStep(tourId, 'form_submit');
  };

  return (
    <BookingContainer>
      <AdventuresSection>
        <Box sx={{
          maxWidth: 1500,
          mx: 'auto',
          px: {
            xs: 1,
            sm: 2,
            md: 3,
            lg: 3,
          }
        }}>
          <CategoryHeader>HoiAnGO Adventures</CategoryHeader>
          <TourGrid $columns={3}>
            {adventureTours.map(tour => (
              <TourCard
                key={tour.id}
                href={queryToDetailTourUrl(tour.tourType, tour.slug, router)}
                //onClick={() => _onClickBookingTour(tour.tourType, tour.slug)}
                sx={{ cursor: 'pointer' }}
              >
                {getBadge(tour) && <Badge>{getBadge(tour)}</Badge>}
                <TourImage
                  src={getThumbnailImage(tour)}
                  alt={getThumbnailImageAlt(tour) || tour.tourContents?.[0]?.title || 'Tour'}
                  sx={{
                    backgroundColor: '#012F5B',
                  }}
                />
                <TourContent>
                  <Box display={'flex'} flexDirection={'column'} gap={0}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        maxLines: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {tour.tourContents?.[0]?.title || 'Tour'}
                    </Typography>
                    <PriceText>
                      {t('from')} <Price price={getMinPriceOfTour(tour)} /> / {t('car')}
                    </PriceText>
                    {
                      tour.tourContents?.[0]?.shortContent ?
                        (
                          <ContentTourText
                            dangerouslySetInnerHTML={{ __html: tour.tourContents?.[0]?.shortContent || '' }}
                            sx={{
                              maxHeight: '8rem !important',
                              overflow: 'hidden !important',
                            }}
                          />
                        ) :
                        null
                    }
                  </Box>

                  <BookButton onClick={(e) => {
                    e.stopPropagation();
                    _onClickBookingTour(tour.tourType, tour.slug);
                  }}>
                    {t('bookTourNow')}
                  </BookButton>
                </TourContent>
              </TourCard>
            ))}
          </TourGrid>
        </Box>
      </AdventuresSection>
      <ExperiencesSection>
        <Box sx={{
          maxWidth: 1500, mx: 'auto', px: {
            xs: 1,
            sm: 2,
            md: 3,
            lg: 3,
          }
        }}>
          <CategoryHeader sx={{ color: '#fff' }}>HoiAnGO Experiences</CategoryHeader>
          <TourGrid $columns={2}>
            {experienceTours.map(tour => (
              <TourCard
                key={tour.id}
                href={queryToDetailTourUrl(tour.tourType, tour.slug, router)}
                //onClick={() => _onClickBookingTour(tour.tourType, tour.slug)}
                sx={{ cursor: 'pointer', background: 'transparent', color: '#fff' }}
              >
                {getBadge(tour) && <Badge>{getBadge(tour)}</Badge>}
                <TourImage
                  src={getThumbnailImage(tour)}
                  alt={getThumbnailImageAlt(tour) || tour.tourContents?.[0]?.title || 'Tour'}
                  sx={{
                    height: {
                      lg: '360px',
                    },
                    backgroundColor: '#012F5B',
                  }} />
                <TourContent sx={{
                  justifyContent: {
                    xs: 'space-between',
                  },
                }}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        mb: 0.5,
                        color: '#fff',
                        fontSize: 24,
                        maxLines: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                      {tour.tourContents?.[0]?.title || 'Tour'}
                    </Typography>
                    <PriceText style={{ color: '#fff', fontWeight: 500, fontSize: 16 }}>
                      {t('from')} <Price price={getMinPriceOfTour(tour)} /> / {t('car')}
                    </PriceText>
                    {tour.tourContents?.[0]?.shortContent ? (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          color: '#fff',
                          opacity: 0.85,
                          minHeight: 48,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        dangerouslySetInnerHTML={{ __html: tour.tourContents?.[0]?.shortContent || '' }}
                      />
                    ) : null}
                  </Box>
                  <BookButton onClick={(e) => {
                    e.stopPropagation();
                    _onClickBookingTour(tour.tourType, tour.slug);
                  }}>
                    {t('bookTourNow')}
                  </BookButton>
                </TourContent>
              </TourCard>
            ))}
          </TourGrid>
        </Box>
      </ExperiencesSection>
    </BookingContainer >
  );
}